import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, XCircle, ShieldAlert, RefreshCcw } from 'lucide-react';
import { adminGetMetaTokenStatus } from '@/lib/api';

/**
 * Shows a banner at the top of the admin dashboard reflecting the current
 * Facebook Page token health. Hidden entirely when the token is OK (so the
 * dashboard stays clean) and visible as a warning/critical/expired card
 * otherwise.
 */
export default function MetaTokenBanner() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = () => {
    setLoading(true);
    adminGetMetaTokenStatus()
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  };

  useEffect(fetchStatus, []);

  if (loading || !status) return null;
  // Never show for healthy tokens — keep the dashboard clean.
  if (status.severity === 'ok') return null;

  const palette = {
    warn: {
      border: 'border-yellow-700/60',
      bg: 'bg-yellow-950/30',
      iconBg: 'bg-yellow-900/40 text-yellow-400',
      heading: 'text-yellow-300',
      Icon: AlertTriangle,
      title: 'Facebook token expiring soon',
    },
    critical: {
      border: 'border-orange-700/70',
      bg: 'bg-orange-950/30',
      iconBg: 'bg-orange-900/40 text-orange-400',
      heading: 'text-orange-300',
      Icon: ShieldAlert,
      title: 'Facebook token expiring very soon',
    },
    expired: {
      border: 'border-red-800/70',
      bg: 'bg-red-950/40',
      iconBg: 'bg-red-900/50 text-red-400',
      heading: 'text-red-300',
      Icon: XCircle,
      title: 'Facebook token expired or invalid',
    },
    unknown: {
      border: 'border-zinc-700',
      bg: 'bg-zinc-900/60',
      iconBg: 'bg-zinc-800 text-zinc-400',
      heading: 'text-zinc-300',
      Icon: CheckCircle2,
      title: 'Facebook token status unknown',
    },
  }[status.severity] || null;

  if (!palette) return null;
  const { Icon } = palette;

  return (
    <div
      className={`glass p-4 border mb-6 ${palette.border} ${palette.bg}`}
      data-testid="meta-token-banner"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`w-10 h-10 flex items-center justify-center rounded-sm flex-shrink-0 ${palette.iconBg}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className={`font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide ${palette.heading}`}>
              {palette.title}
            </p>
            <p className="text-zinc-300 text-sm mt-1" data-testid="meta-token-message">
              {status.message}
            </p>
            <p className="text-zinc-500 text-xs mt-2">
              Generate a new long-lived Page token in Meta Business Suite and update{' '}
              <code className="text-zinc-400">FACEBOOK_PAGE_TOKEN</code> in your deployment env to keep
              Facebook reviews &amp; posts working.
            </p>
          </div>
        </div>
        <button
          onClick={fetchStatus}
          className="flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-wider border border-zinc-700 px-3 py-1.5 rounded-sm"
          data-testid="meta-token-refresh"
          type="button"
        >
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>
    </div>
  );
}
